/**
 * Protected Route Guard
 * Redirects to login if not authenticated
 */

import { useEffect } from 'react'
import { useLocation, useNavigate, useSelector } from '@spcc/core'
import { selectIsAuthenticated, selectAuthIsInitialized, selectIsAuthLoading } from '../../store'

interface ProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
}

/**
 * Wrapper component that protects routes from unauthenticated access
 */
export function ProtectedRoute({
  children,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isInitialized = useSelector(selectAuthIsInitialized)
  const isLoading = useSelector(selectIsAuthLoading)

  // Redirect to login if not authenticated
  // Only redirect if we're not already on the login page to prevent loops
  useEffect(() => {
    if (isInitialized && !isAuthenticated && location.pathname !== redirectTo) {
      navigate({
        to: redirectTo,
        search: { from: location.pathname },
        replace: true,
      })
    }
  }, [isInitialized, isAuthenticated, navigate, redirectTo, location.pathname])

  // Show loading ONLY while auth is initializing, NOT for general loading
  // Once initialized, allow rendering even if loading (portal init, etc.)
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  // Don't render protected content if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return <>{children}</>
}
